import React, { useState, useEffect, useMemo } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Dropdown,
  Option,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Edit24Regular,
  Delete24Regular,
  Person24Regular,
  Box24Regular,
  Document24Regular,
  Briefcase24Regular,
  Payment24Regular,
  ClipboardTask24Regular,
} from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { Activity } from '../types/activity';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDate } from '../lib/businessTime';

const useStyles = makeStyles({
  container: {
    paddingTop: '16px',
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  filterBar: {
    display: 'flex',
    gap: '16px',
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  dataTableWrapper: {
    '& > div': {
      boxShadow: 'none',
      borderRadius: '0',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px'),
  },
  actionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    fontSize: '12px',
    fontWeight: '600',
  },
  actionCreate: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  actionUpdate: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  actionDelete: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  entityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  entityIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorNeutralForeground3,
  },
  changesCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  changeItem: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  changeField: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
});

const ActivityFeed: React.FC = () => {
  const styles = useStyles();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');

  // Fetch activities
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);

      try {
        let url = `/activities?limit=100`;
        if (entityTypeFilter !== 'all') {
          url += `&entity_type=${entityTypeFilter}`;
        }
        if (actionFilter !== 'all') {
          url += `&action=${actionFilter}`;
        }

        const response = await apiFetch(url);
        const data = await response.json();

        if (data.success) {
          setActivities(data.data);
        } else {
          setError(data.error || 'Failed to load activities');
        }
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError('Failed to load activities');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [entityTypeFilter, actionFilter]);

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'order':
        return <Box24Regular />;
      case 'customer':
        return <Person24Regular />;
      case 'task':
        return <ClipboardTask24Regular />;
      case 'document':
        return <Document24Regular />;
      case 'payment':
        return <Payment24Regular />;
      default:
        return <Briefcase24Regular />;
    }
  };

  const getActionStyle = (action: string) => {
    switch (action) {
      case 'create':
        return styles.actionCreate;
      case 'update':
        return styles.actionUpdate;
      case 'delete':
        return styles.actionDelete;
      default:
        return styles.actionUpdate;
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create':
        return <Add24Regular />;
      case 'update':
        return <Edit24Regular />;
      case 'delete':
        return <Delete24Regular />;
      default:
        return <Edit24Regular />;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatBusinessDate(dateString);
  };

  const columns: ColumnConfig<Activity>[] = useMemo(() => [
    {
      columnKey: 'createdAt',
      label: 'Time',
      width: '120px',
      sortable: true,
      render: (item) => (
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          {formatDateTime(item.createdAt)}
        </Text>
      ),
    },
    {
      columnKey: 'entityType',
      label: 'Entity',
      width: '140px',
      sortable: true,
      groupable: true,
      render: (item) => (
        <div className={styles.entityCell}>
          <span className={styles.entityIcon}>{getEntityIcon(item.entityType)}</span>
          <Text size={200} style={{ textTransform: 'capitalize' }}>
            {item.entityType}
          </Text>
        </div>
      ),
    },
    {
      columnKey: 'action',
      label: 'Action',
      width: '100px',
      sortable: true,
      groupable: true,
      render: (item) => (
        <span className={`${styles.actionBadge} ${getActionStyle(item.action)}`}>
          {item.action.charAt(0).toUpperCase() + item.action.slice(1)}
        </span>
      ),
    },
    {
      columnKey: 'description',
      label: 'Description',
      sortable: false,
      render: (item) => (
        <div>
          <Text size={200}>{item.description}</Text>
          {item.changes && item.changes.length > 0 && (
            <div className={styles.changesCell}>
              {item.changes.slice(0, 3).map((change, index) => (
                <span key={index} className={styles.changeItem}>
                  <span className={styles.changeField}>{change.field}</span>
                  {change.oldValue !== undefined && (
                    <>: {String(change.oldValue)} → </>
                  )}
                  {String(change.newValue)}
                </span>
              ))}
              {item.changes.length > 3 && (
                <span className={styles.changeItem}>
                  +{item.changes.length - 3} more changes
                </span>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      columnKey: 'actorName',
      label: 'Actor',
      width: '140px',
      sortable: true,
      groupable: true,
      render: (item) => (
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          {item.actorName}
        </Text>
      ),
    },
  ], []);

  return (
    <div className={styles.container}>
        <div className={styles.tableContainer}>
          {/* Filter Bar */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Entity Type:</span>
              <Dropdown
                value={entityTypeFilter === 'all' ? 'All' : entityTypeFilter.charAt(0).toUpperCase() + entityTypeFilter.slice(1)}
                selectedOptions={[entityTypeFilter]}
                onOptionSelect={(_, data) => setEntityTypeFilter(data.optionValue as string)}
                style={{ minWidth: '140px' }}
              >
                <Option value="all">All</Option>
                <Option value="order">Order</Option>
                <Option value="task">Task</Option>
                <Option value="customer">Customer</Option>
                <Option value="document">Document</Option>
                <Option value="payment">Payment</Option>
              </Dropdown>
            </div>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Action:</span>
              <Dropdown
                value={actionFilter === 'all' ? 'All' : actionFilter.charAt(0).toUpperCase() + actionFilter.slice(1)}
                selectedOptions={[actionFilter]}
                onOptionSelect={(_, data) => setActionFilter(data.optionValue as string)}
                style={{ minWidth: '120px' }}
              >
                <Option value="all">All</Option>
                <Option value="create">Create</Option>
                <Option value="update">Update</Option>
                <Option value="delete">Delete</Option>
              </Dropdown>
            </div>
          </div>

          {/* Data Table */}
          <div className={styles.dataTableWrapper}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label="Loading activity feed..." />
              </div>
            ) : error ? (
              <div className={styles.loadingContainer}>
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
              </div>
            ) : (
              <DataTable
                data={activities}
                columns={columns}
                keyField="id"
                hideableColumns
                columnVisibilityStorageKey="activity_feed"
                itemsPerPage={25}
              />
            )}
          </div>
        </div>
    </div>
  );
};

export default ActivityFeed;
